import { lazy } from 'react';
import { Navigate } from 'react-router-dom';

// project imports
import MainLayout from 'layout/MainLayout';
import Loadable from 'ui-component/Loadable';
import AuthGuard from 'utils/route-guard/AuthGuard';
import ErrorBoundary from './ErrorBoundary';

// sample page routing
const SamplePage = Loadable(lazy(() => import('views/sample-page')));

// FAQ page routing
const FAQ = Loadable(lazy(() => import('views/pages/faq/FAQ')));
import FAQList from 'views/pages/faq/FAQList';

// AUDIT LOG page routing
const AuditLogPage = Loadable(lazy(() => import('views/audit/AuditLogPage')));
const Calendar = Loadable(lazy(() => import('views/calendar')));

// application - user social & account profile routing
const AppUserSocialProfile = Loadable(lazy(() => import('views/users/social-profile')));
const AppUserAccountProfile2 = Loadable(lazy(() => import('views/users/account-profile/Profile2')));
const AppKanban = Loadable(lazy(() => import('views/kanban')));
const UserMembershipPage = Loadable(lazy(() => import('views/pages/userlist/UserMembershipPage')));
const UserFeePage = Loadable(lazy(() => import('views/pages/userlist/UserFeePage')));
const AppKanbanBacklogs = Loadable(lazy(() => import('views/kanban/Backlogs')));
const AppKanbanBoard = Loadable(lazy(() => import('views/kanban/Board')));

// Creator
const CreatorFeatureType = Loadable(lazy(() => import('views/creator/FeatureType')));
const CreatorFeaureModule = Loadable(lazy(() => import('views/creator/FeatureModule')));
const CreatorFeature = Loadable(lazy(() => import('views/creator/Feature')));

// CBO & CNAE (CRUD)
const Cbo = Loadable(lazy(() => import('views/company/Cbo.jsx')));
const Cnae = Loadable(lazy(() => import('views/cnae/Cnae.jsx')));
const CnaeDivision = Loadable(lazy(() => import('views/cnae/CnaeDivision.jsx')));
const CnaeGroup = Loadable(lazy(() => import('views/cnae/CnaeGroup.jsx')));
const CnaeSession = Loadable(lazy(() => import('views/cnae/CnaeSession.jsx')));

const SendMessage = Loadable(lazy(() => import('views/communication/SendMessage.jsx')));

// Flows (ensure default exports exist)
const ProfessionalFlow = Loadable(lazy(() => import('views/professional/ProfessionalFlow.jsx')));
const CompanyFlow = Loadable(lazy(() => import('views/company/CompanyFlow.jsx')));
const UserFlow = Loadable(lazy(() => import('views/users/UserFlow.jsx')));

const UrbanismBlock1 = Loadable(lazy(() => import('views/urbanism/UrbanismBlock1.jsx')));
const UrbanismBlock2 = Loadable(lazy(() => import('views/urbanism/UrbanismBlock2.jsx')));
const UrbanismBlock3 = Loadable(lazy(() => import('views/urbanism/UrbanismBlock3.jsx')));
const UrbanismBlock4 = Loadable(lazy(() => import('views/urbanism/UrbanismBlock4.jsx')));
const UrbanismBlock5 = Loadable(lazy(() => import('views/urbanism/UrbanismBlock5.jsx')));
const UrbanismBlock6 = Loadable(lazy(() => import('views/urbanism/UrbanismBlock6.jsx')));

const MapPage = Loadable(lazy(()=> import('views/pages/mapa/MapPage')))

const Project = Loadable(lazy(() => import('views/pages/Project.jsx')));

const FastapiRails = Loadable(lazy(() => import('views/pages/FastapiRails.jsx')));

const UserEdit = Loadable(lazy(() => import('views/pages/authentication/Register.jsx')));
const UserNew = Loadable(lazy(() => import('views/pages/authentication/Register.jsx')));

const UserList = Loadable(lazy(() => import('views/pages/userlist')));
const UserDetailPage = Loadable(lazy(() => import('views/pages/userlist/UserDetailPage')));
const UserEmailPage = Loadable(lazy(() => import('views/pages/userlist/UserEmailPage')));
const UserConfirmEmailPage = Loadable(lazy(() => import('views/pages/userlist/UserConfirmEmailPage')));
const UserBlockPage = Loadable(lazy(() => import('views/pages/userlist/UserBlockPage')));
const UserStatusPage = Loadable(lazy(() => import('views/pages/userlist/UserStatusPage')));
const UserPermissionPage = Loadable(lazy(() => import('views/pages/userlist/UserPermissionPage')));

const ProcessList = Loadable(lazy(() => import('views/pages/ProcessList.jsx')));

const Org = Loadable(lazy(() => import('views/pages/Org.jsx')));

const Permission = Loadable(lazy(() => import('views/pages/Permission.jsx')));

const Payment = Loadable(lazy(() => import('views/pages/Payment.jsx')));

const Field = Loadable(lazy(() => import('views/pages/Field.jsx')));

const Form = Loadable(lazy(() => import('views/pages/Form.jsx')));

const Document = Loadable(lazy(() => import('views/pages/Document.jsx')));
const Status = Loadable(lazy(() => import('views/pages/Status.jsx')));

const UserLog = Loadable(lazy(() => import('views/pages/user/Log.jsx')));
const PrivacyPolicy = Loadable(lazy(() => import('views/pages/PrivacyPolicy.jsx')));

const DocumentGalery = Loadable(lazy(() => import('views/pages/DocumentGalery.jsx')));

const AuthMarketing = Loadable(lazy(() => import('views/pages/Marketing.jsx')));

const Background = Loadable(lazy(() => import('views/pages/Background.jsx')));

const Setup = Loadable(lazy(() => import('views/pages/Setup.jsx')));
const TemplateSettings = Loadable(lazy(() => import('views/pages/TemplateSettings.jsx')));

const LogoutPage = Loadable(lazy(() => import('views/users/Logout.jsx')));

const ProjectDynamicNew = Loadable(lazy(() => import('views/pages/projects/dynamic_new.jsx')));
const ProjectDynamicShow = Loadable(lazy(() => import('views/pages/projects/dynamic_show.jsx')));
const ProjectDynamicList = Loadable(lazy(() => import('views/pages/projects/dynamic_list.jsx')));
const ProjectCentral = Loadable(lazy(() => import('views/pages/projects/Central.jsx')));
const ProjectSearch = Loadable(lazy(() => import('views/pages/projects/Search.jsx')));
const StringTemplateList = Loadable(lazy(() => import('views/pages/StringTemplateList.jsx')));
const IconFunctionList = Loadable(lazy(() => import('views/pages/IconFunctionList.jsx')));
const DbSync = Loadable(lazy(() => import('views/pages/DbSync.jsx')));
const EmailContentTemplateList = Loadable(lazy(() => import('views/pages/EmailContentTemplateList.jsx')));
const ProjectTypeList = Loadable(lazy(() => import('views/pages/ProjectTypeList.jsx')));
const FlowList = Loadable(lazy(() => import('views/pages/FlowList.jsx')));
const ProjectUnderAnalysis = Loadable(lazy(() => import('views/pages/projects/under_analysis.jsx')));
const ProjectProcessing = Loadable(lazy(() => import('views/pages/projects/processing.jsx')));
const ProjectUpload = Loadable(lazy(() => import('views/pages/projects/Upload.jsx')));
const ProjectSupport = Loadable(lazy(() => import('views/pages/projects/Support.jsx')));
const ProjectModify = Loadable(lazy(() => import('views/pages/projects/Modify.jsx')));
const ProjectCancel = Loadable(lazy(() => import('views/pages/projects/Cancel.jsx')));
const ProjectPayment = Loadable(lazy(() => import('views/pages/projects/PaymentModal.jsx').then((m) => ({ default: m.ProjectPayment }))));
const ProjectWhatsapp = Loadable(lazy(() => import('views/pages/projects/WhatsAppModal.jsx').then((m) => ({ default: m.ProjectWhatsapp }))));
const TaskTypeList = Loadable(lazy(() => import('views/pages/TaskTypeList.jsx')));

const MainRoutes = {
  path: '/',
  element: (
    <AuthGuard>
      <MainLayout />
    </AuthGuard>
  ),
  errorElement: <ErrorBoundary />,
  children: [
    { path: '/task_types', element: <TaskTypeList /> },
    { path: '/project/upload/:id', element: <ProjectUpload /> },
    { path: '/project/support/:id', element: <ProjectSupport /> },
    { path: '/project/processing/:id', element: <ProjectProcessing /> },
    { path: '/project/under_analysis/:id', element: <ProjectUnderAnalysis /> },
    { path: '/project/modify/:id', element: <ProjectModify /> },
    { path: '/project/cancel/:id', element: <ProjectCancel /> },
    { path: '/project/payment/:id', element: <ProjectPayment /> },
    { path: '/project/whatsapp/:id', element: <ProjectWhatsapp /> },
    { path: '/flow', element: <FlowList /> },
    { path: '/project_types', element: <ProjectTypeList /> },
    { path: '/string_templates', element: <StringTemplateList /> },
    { path: '/icon_functions', element: <IconFunctionList /> },
    { path: '/db_sync', element: <DbSync /> },
    { path: '/email_content_templates', element: <EmailContentTemplateList /> },
    { path: '/project/dynamic_list', element: <ProjectDynamicList /> },
    { path: '/project/central', element: <ProjectCentral /> },
    { path: '/project/search', element: <ProjectSearch /> },
    { path: '/project/dynamic_show/:id', element: <ProjectDynamicShow /> },
    { path: '/sample-page', element: <SamplePage /> },
    { path: '/faq', element: <FAQ /> },
    { path: '/faq-list', element: <FAQList /> },
    { path: '/audit/log', element: <AuditLogPage /> },
    { path: '/apps/user/account-profile/profile2', element: <AppUserAccountProfile2 /> },
    { path: '/calendar', element: <Calendar /> },
    { path: '/project/dynamic_new', element: <ProjectDynamicNew /> },
    {
      path: '/kanban',
      element: <AppKanban />,
      children: [
        { path: 'backlogs', element: <AppKanbanBacklogs /> },
        { path: 'board', element: <AppKanbanBoard /> }
      ]
    },
    { path: '/creator/feature', element: <CreatorFeature /> },
    { path: '/creator/feature-module', element: <CreatorFeaureModule /> },
    { path: '/creator/feature-type', element: <CreatorFeatureType /> },

    // CBO & CNAE pages
    { path: '/professional/cbo', element: <Cbo /> },
    { path: '/cnae', element: <Cnae /> },
    { path: '/cnae/division', element: <CnaeDivision /> },
    { path: '/cnae/group', element: <CnaeGroup /> },
    { path: '/cnae/session', element: <CnaeSession /> },

    { path: '/communication/send-message', element: <SendMessage /> },

    // New flow routes
    { path: '/professional/flow', element: <ProfessionalFlow /> },
    { path: '/company/flow', element: <CompanyFlow /> },
    { path: '/user/flow', element: <UserFlow /> },
    { path: '/urbanism/block-1', element: <UrbanismBlock1 /> },
    { path: '/urbanism/block-2', element: <UrbanismBlock2 /> },
    { path: '/urbanism/block-3', element: <UrbanismBlock3 /> },
    { path: '/urbanism/block-4', element: <UrbanismBlock4 /> },
    { path: '/urbanism/block-5', element: <UrbanismBlock5 /> },
    { path: '/urbanism/block-6', element: <UrbanismBlock6 /> },

    {
      path: '/mapa',
      element: <MapPage />
    },
    {
      path: '/documents',
      element: <Document />
    },
    {
      path: '/status',
      element: <Status />
    },
    {
      path: '/project',
      element: <Project />
    },
    {
      path: '/fastapi-rails',
      element: <FastapiRails />
    },
    {
      path: '/user-list',
      element: <UserList />
    },
    {
      path: '/user-list/:id',
      element: <UserDetailPage />
    },
    {
      path: '/user-list/:id/email',
      element: <UserEmailPage />
    },
    {
      path: '/user-list/:id/confirm-email',
      element: <UserConfirmEmailPage />
    },
    {
      path: '/user-list/:id/block',
      element: <UserBlockPage />
    },
    {
      path: '/user-list/:id/status',
      element: <UserStatusPage />
    },
    {
      path: '/user-list/:id/permission',
      element: <UserPermissionPage />
    },
    {
      path: '/user-list/:id/membership',
      element: <UserMembershipPage />
    },
    {
      path: '/user-list/:id/fee',
      element: <UserFeePage />
    },
    {
      path: '/process-list',
      element: <ProcessList />
    },
    {
      path: '/permission',
      element: <Permission />
    },
    {
      path: '/payment',
      element: <Payment />
    },
    {
      path: '/field',
      element: <Field />
    },
    {
      path: '/form',
      element: <Form />
    },
    {
      path: '/document',
      element: <Document />
    },
    {
      path: '/user/edit',
      element: <UserEdit />
    },
    {
      path: '/user/log',
      element: <UserLog />
    },
    {
      path: '/user/logs/:userId',
      element: <UserLog />  // Same component, filters by userId param
    },
    {
      path: '/privacidade',
      element: <PrivacyPolicy />
    },
    {
      path: 'users/new',
      element: <UserNew />
    },
    {
      path: 'document/galery',
      element: <DocumentGalery />
    },
    {
      path: '/marketing',
      element: <AuthMarketing />
    },
    {
      path: '/background',
      element: <Background />
    },
    {
      path: '/setup',
      element: <Setup />
    },
    {
      path: '/template_settings',
      element: <TemplateSettings />
    },
    {
      path: '/logout',
      element: <LogoutPage />
    },
    {
      path: '/users/sign_out',
      element: <Navigate to="/logout" replace />
    },
    {
      path: '/users/logout',
      element: <Navigate to="/logout" replace />
    },
    {
      path: '/org',
      element: <Org />
    },
  ]
};

export default MainRoutes;
